import React from 'react';
import { Hero } from './components/Hero';
import { TransformationCards } from './components/TransformationCards';
import { ResultsSection } from './components/ResultsSection';
import { Button } from './components/Button';
import { CheckCircle } from 'lucide-react';

function App() {
  const consultationLink = "https://kronologic.me/1-click/zWxqVT2ASOytpMCtSReRDWxbBRVHiUbHjzqAm5x3Arg";

  return (
    <div className="min-h-screen bg-gray-50">
      <Hero consultationLink={consultationLink} />
      <TransformationCards />

      {/* Main Content */}
      <section className="py-32 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="prose prose-lg max-w-none">
            {/* First Section */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-16 rounded-2xl mb-32 shadow-sm">
              <h2 className="text-5xl font-extrabold text-blue-900 mb-8 leading-tight">
                Your challenge: <br /> TURN DATA INTO BUSINESS VALUE
              </h2>
              <div className="space-y-8">
                <p className="text-xl text-blue-800/80 leading-relaxed">
                  Today's clients seek the "why" behind behaviors. Together, we can reveal these insights in your journey maps.
                </p>
              </div>
              <div className="mt-12 text-center">
                <Button primary href={consultationLink} className="text-xl px-12 py-5">Partner With Us</Button>
              </div>
            </div>

            {/* ConsumerDNA Section */}
            <div className="mb-32">
              <h2 className="text-5xl font-extrabold text-gray-900 mb-8 leading-tight text-center">
                ConsumerDNA™: Your guide to revealing the human story behind the data
              </h2>
              <div className="space-y-8">
                <p className="text-xl text-gray-600 leading-relaxed">
                  ConsumerDNA™ is a bespoke intelligence service that enhances your existing journey mapping approach—never replacing it. We reveal the hidden drivers of customer decisions that analytics alone miss, giving your deliverables the depth that drives real business impact.
                </p>
              </div>
            </div>

            {/* Empowerment Section */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-16 rounded-2xl mb-32 shadow-sm">
              <h2 className="text-5xl font-extrabold text-gray-900 mb-12">How ConsumerDNA™ empowers your consultancy:</h2>
              <ul className="space-y-8">
                {[
                  'Leverage advanced insights without building specialized in-house capabilities',
                  'Create premium offerings that justify higher rates and expanded scope',
                  'Transform existing client relationships into longer-term strategic engagements',
                  'Win more competitive pitches with insights that demonstrate superior customer understanding',
                  'Uncover customer motivations that analytics alone cannot reveal',
                  'Establish thought leadership with compelling insights that elevate your content marketing'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-6">
                    <CheckCircle className="w-8 h-8 text-emerald-500 flex-shrink-0 mt-1" />
                    <span className="text-xl text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-16 text-center">
                <Button primary href={consultationLink} className="text-xl px-12 py-5">Partner With Us</Button>
              </div>
            </div>

            <ResultsSection />

            {/* Partnership Process Section */}
            <div className="mb-32">
              <h2 className="text-5xl font-extrabold text-gray-900 mb-8 leading-tight text-center">
                Unlock Customer Psychology: Our Four-Step Partnership Process
              </h2>
              <div className="relative">
                <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-blue-200"></div>
                
                <div className="space-y-8">
                  {[
                    {
                      number: 1,
                      title: "Discovery & Integration",
                      description: "We work with your team to understand your existing methodologies and client needs. ConsumerDNA™ is designed to enhance, not replace, your current approaches—integrating seamlessly with your journey maps and analytics."
                    },
                    {
                      number: 2,
                      title: "Customer Motivation Analysis",
                      description: "Your consultancy maintains complete client relationship ownership while our behind-the-scenes analysis enhances your deliverables. You maintain control while delivering insights that transform how clients understand their customers."
                    },
                    {
                      number: 3,
                      title: "Insight Translation",
                      description: "We transform complex psychology into clear, actionable insights your team can confidently present to clients. You receive strategic interpretations ready for immediate implementation with your clients."
                    },
                    {
                      number: 4,
                      title: "Success Measurement Framework",
                      description: "We provide your team with measurement frameworks to track and quantify the impact of psychological insights on client outcomes. This enables you to demonstrate concrete ROI, build case studies, and expand ConsumerDNA™ applications across your client portfolio."
                    }
                  ].map((step) => (
                    <div key={step.number} className="relative pl-24">
                      <div className="absolute left-0 w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center text-2xl font-bold shadow-lg">
                        {step.number}
                      </div>
                      <div className="bg-white p-8 rounded-2xl shadow-sm relative">
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-4 h-4 rotate-45 bg-white"></div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-4">{step.title}</h3>
                        <p className="text-lg text-gray-600 leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="text-center mb-32">
              <Button primary href={consultationLink} className="text-xl px-16 py-6">
                Start Your Partnership Journey
              </Button>
            </div>

            {/* Comparison Section */}
            <div className="grid md:grid-cols-2 gap-12 mb-32">
              <div className="bg-gradient-to-br from-red-50 to-red-100 p-16 rounded-2xl shadow-sm">
                <h3 className="text-3xl font-extrabold text-red-900 mb-8">Without deeper customer understanding:</h3>
                <ul className="space-y-6">
                  <li className="text-xl text-red-800/80">Your recommendations address symptoms rather than root causes, leading to limited improvement in client metrics</li>
                  <li className="text-xl text-red-800/80">Differentiation becomes increasingly difficult as journey mapping and analytics become commoditized</li>
                  <li className="text-xl text-red-800/80">Client questions about customer motivation continue to challenge your team's credibility</li>
                  <li className="text-xl text-red-800/80">Your practice risks being seen as implementers rather than strategic advisors</li>
                  <li className="text-xl text-red-800/80">Your practice risks being perceived as interchangeable with lower-cost providers focused solely on collecting feedback</li>
                </ul>
              </div>
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-16 rounded-2xl shadow-sm">
                <h3 className="text-3xl font-extrabold text-emerald-900 mb-8">With ConsumerDNA™ as your guide:</h3>
                <ul className="space-y-6">
                  <li className="text-xl text-emerald-800/80">You deliver insights that trigger "aha moments" for clients about their customers' true motivations</li>
                  <li className="text-xl text-emerald-800/80">Your practice stands out with a capability that few competitors can match</li>
                  <li className="text-xl text-emerald-800/80">Executive conversations shift from tactical metrics to strategic customer understanding</li>
                  <li className="text-xl text-emerald-800/80">Implementation success rates improve as solutions address psychological barriers</li>
                  <li className="text-xl text-emerald-800/80">Your practice becomes known for the transformative customer insights that drive business results, not just reporting on what happens</li>
                </ul>
              </div>
            </div>

            {/* Final CTA Section */}
            <div className="text-center mb-32">
              <div className="bg-gradient-to-br from-blue-900 to-blue-800 p-16 rounded-2xl shadow-lg">
                <h2 className="text-4xl font-extrabold mb-8 leading-tight text-white">
                  The future of CX consulting belongs to firms that transform customer data into actionable insights that drive business outcomes. Your consultancy deserves this competitive advantage.
                </h2>
                <p className="text-xl text-blue-100 mb-12">
                  Take the next step: Schedule a confidential partnership conversation to explore how ConsumerDNA™ enhances your unique methodology while protecting your client relationships.
                </p>
                <Button 
                  primary 
                  href={consultationLink} 
                  className="text-xl px-16 py-6 bg-blue-50 text-blue-900 hover:bg-white hover:text-blue-900"
                >
                  Partner With Us
                </Button>
              </div>
            </div>

            <p className="text-center text-gray-500 italic text-xl">
              ConsumerDNA™ is a service of ICP EDGE, specialists in applying behavioral science to customer experience.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default App;